import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Calendar, MapPin, MessageCircle, Tag, UploadCloud, Image, QrCode, UserPlus, Timer, BarChart3 } from "lucide-react";
import { formatEventDateRange, getChatStatus } from "../../utils/eventPermissions";
import Card from "../common/Card";
import IconTile from "../common/IconTile";
import { color } from "../../styles/theme";

const MAX_DESCRIPTION_CHARS = 220;

/**
 * Details tab — event summary, who-can-upload readback, quick actions.
 * Ported from mobile's details-tab/index.js + event-summary.js +
 * event-description.js + quick-actions.js + who-can-upload/index.js,
 * scoped to promoter/owner-relevant content (no join/chat-CTA/eQR-banner —
 * those are regular-user flows, out of scope for this panel).
 */
const DetailsTab = ({ event, permissions, onOpenGallery, onOpenQR, onOpenInviteBA, onOpenQRBufferTime }) => {
  const { t } = useTranslation("eventDetails");
  const { isAdmin, canManageAccess, canManageGallery, canManageSingleMedia, isFamilyEvent, isARoomEvent, isPrivateEvent, isNonPublicEvent, isPublicEvent } = permissions;
  const [descExpanded, setDescExpanded] = useState(false);
  const navigate = useNavigate();

  const chatStatus = useMemo(() => getChatStatus(event), [event]);

  const eventTypeLabel = isPublicEvent
    ? t("detailsTab.eventType.public")
    : isPrivateEvent
      ? t("detailsTab.eventType.private")
      : isNonPublicEvent
        ? t("detailsTab.eventType.nonPublic")
        : "";

  const description = event?.Description || "";
  const isLongDescription = description.length > MAX_DESCRIPTION_CHARS;
  const displayDescription = descExpanded || !isLongDescription
    ? description
    : `${description.slice(0, MAX_DESCRIPTION_CHARS)}…`;

  const whoCanUploadLabel = event?.UploaderRole ? t("detailsTab.whoCanUpload.promoterOnly") : t("detailsTab.whoCanUpload.promoterAndUsers");

  const canInviteBA = isAdmin && !isFamilyEvent && !isARoomEvent && !isNonPublicEvent && !isPrivateEvent;
  const canShowQR = canManageAccess && !isPrivateEvent;
  const canOpenGallery = canManageGallery || canManageSingleMedia;

  return (
    <div>
      <Card style={s.card}>
        <InfoRow icon={Calendar} text={formatEventDateRange(event)} />
        {event?.Location && (
          <InfoRow
            icon={MapPin}
            text={<>{event.Location}{event?.Address && event.Address !== event.Location && <span style={s.rowSub}> — {event.Address}</span>}</>}
          />
        )}
        {eventTypeLabel && (
          <InfoRow icon={Tag} text={`${eventTypeLabel}${event?.Category ? ` · ${event.Category}` : ""}`} />
        )}
        {chatStatus.chatStatus && (
          <InfoRow icon={MessageCircle} text={chatStatus.chatStatus} tone={chatStatus.isChatEnabled ? "success" : "muted"} last={event?.Origin !== "Private"} />
        )}
        {event?.Origin === "Private" && (
          <InfoRow icon={UploadCloud} text={<>{t("detailsTab.whoCanUpload.prefix")} <strong>{whoCanUploadLabel}</strong></>} last />
        )}
      </Card>

      <div style={s.actionsRow}>
        {canOpenGallery && <IconTile icon={Image} label={t("detailsTab.actions.gallery")} onClick={onOpenGallery} tone="primary" />}
        {canShowQR && <IconTile icon={QrCode} label={t("detailsTab.actions.qrCodes")} onClick={onOpenQR} tone="info" />}
        {canInviteBA && <IconTile icon={UserPlus} label={t("detailsTab.actions.inviteAssistant")} onClick={onOpenInviteBA} tone="success" />}
        {canShowQR && <IconTile icon={Timer} label={t("detailsTab.actions.qrBufferTime")} onClick={onOpenQRBufferTime} tone="warning" />}
        {isAdmin && (
          <IconTile icon={BarChart3} label={t("detailsTab.actions.mediaHistory")} onClick={() => navigate(`/events/${event?.Id}/media-history`)} tone="neutral" />
        )}
      </div>

      {!isFamilyEvent && description && (
        <Card style={s.descCard}>
          <h4 style={s.sectionTitle}>{t("detailsTab.description.title")}</h4>
          <p style={s.description}>{displayDescription}</p>
          {isLongDescription && (
            <button type="button" style={s.readMoreBtn} onClick={() => setDescExpanded((v) => !v)}>
              {descExpanded ? t("detailsTab.description.readLess") : t("detailsTab.description.readMore")}
            </button>
          )}
        </Card>
      )}
    </div>
  );
};

const TONE_COLOR = { success: color.success500, muted: color.textMuted, default: color.textPrimary };

const InfoRow = ({ icon: Icon, text, tone = "default", last = false }) => (
  <div style={{ ...s.row, ...(last ? { borderBottom: "none" } : {}) }}>
    <span style={s.icon}><Icon size={16} strokeWidth={2} color={color.gray500} /></span>
    <span style={{ color: TONE_COLOR[tone] || TONE_COLOR.default, fontWeight: tone === "success" ? 600 : 400 }}>{text}</span>
  </div>
);

const s = {
  card: { padding: 0, marginBottom: 16 },
  row: {
    display: "flex", alignItems: "flex-start", gap: 12, fontSize: 13.5, color: color.textPrimary,
    padding: "13px 20px", borderBottom: `1px solid ${color.gray100}`,
  },
  icon: { width: 20, flexShrink: 0, display: "flex", alignItems: "center", paddingTop: 1 },
  rowSub: { color: color.textMuted },
  actionsRow: { display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" },
  descCard: { marginBottom: 16 },
  sectionTitle: { margin: "0 0 10px", fontSize: 13.5, fontWeight: 700, color: color.textPrimary },
  description: { margin: 0, fontSize: 13.5, color: color.textSecondary, lineHeight: 1.65, whiteSpace: "pre-wrap" },
  readMoreBtn: {
    background: "none", border: "none", color: color.primary600, fontWeight: 600,
    fontSize: 12.5, cursor: "pointer", padding: 0, marginTop: 10,
  },
};

export default DetailsTab;
